import { useCallback, useEffect, useMemo, useRef, useState, type InputHTMLAttributes, type ReactNode } from 'react'
import {
  addressFieldLabels,
  addressStatusBadge,
  zipNeedsAttention,
  composeAddress,
  emptyAddressParts,
  isAddressPartsComplete,
  loadThaiAddressDb,
  loadedThaiAddressDb,
  parseThaiAddress,
  resolveAddressSelection,
  samePartsValue,
  type AddressParts,
  type ThaiAddressDb,
} from '../../lib/thaiAddress'
import { THAI_ADDRESS_URL, addressPartsOfText, rememberAddressParts } from '../../lib/thaiAddressText'
import '../../styles/thai-address.css'

type Notice = { kind: 'parsed' | 'unparsed'; original: string } | null

function squash(text: string): string {
  return String(text || '').replace(/\s+/g, '')
}

function useThaiAddressDb(): { db: ThaiAddressDb | null; failed: boolean } {
  const [db, setDb] = useState<ThaiAddressDb | null>(() => loadedThaiAddressDb())
  const [failed, setFailed] = useState(false)
  useEffect(() => {
    if (db) return
    let alive = true
    loadThaiAddressDb(THAI_ADDRESS_URL)
      .then((loaded) => { if (alive) setDb(loaded) })
      .catch(() => { if (alive) setFailed(true) })
    return () => { alive = false }
  }, [db])
  return { db, failed }
}

type Props = {
  /** id ของช่องบ้านเลขที่ (คง id เดิมของ textarea เพื่อให้การเลื่อนไปช่องที่ขาดยังทำงาน) */
  id: string
  /** ข้อความเต็มเดิม (form.address) */
  value: string
  onChange: (text: string) => void
  disabled?: boolean
  /** props เสริมของช่องบ้านเลขที่ (กัน iOS autofill · เปิด Autocomplete) */
  lineProps?: InputHTMLAttributes<HTMLInputElement>
  onLineTyping?: () => void
  /** รายการ Autocomplete ใต้ช่องบ้านเลขที่ */
  lineAddon?: ReactNode
  lineWrapClassName?: string
  /** 'sheet' = แผ่นข้อมูลสมาชิก (สไตล์ .mo-address-form) */
  variant?: 'order' | 'sheet'
  /** หัวข้อ เช่น "ที่อยู่จัดส่ง" — ป้ายสถานะต่อท้ายหัวข้อ */
  title?: string
}

/**
 * ที่อยู่ 5 ช่อง (บ้านเลขที่ · รหัสไปรษณีย์ · จังหวัด · อำเภอ · ตำบล) — กรอกรหัสแล้วเลือกให้อัตโนมัติ
 * รับ/ส่งเป็นข้อความเต็ม: ข้อความที่ตั้งจากที่อื่น (Autocomplete / ข้อมูลสมาชิก) แยกเป็นช่องให้เอง
 */
export function ThaiAddressInput({ id, value, onChange, disabled, lineProps, onLineTyping, lineAddon, lineWrapClassName, variant = 'order', title }: Props) {
  const { db, failed } = useThaiAddressDb()
  const [parts, setParts] = useState<AddressParts>(emptyAddressParts)
  const [notice, setNotice] = useState<Notice>(null)
  /** ข้อความล่าสุดที่ช่องนี้แยก/ประกอบเอง — ค่าเดิมซ้ำไม่ต้องแยกใหม่ */
  const handled = useRef<string | null>(null)

  const emit = useCallback((next: AddressParts) => {
    setParts(next)
    const text = composeAddress(next)
    handled.current = text
    rememberAddressParts(text, next)
    if (text !== value) onChange(text)
  }, [onChange, value])

  // ข้อความเปลี่ยนจากที่อื่น → แยกเป็นช่อง
  useEffect(() => {
    if (handled.current !== null && squash(handled.current) === squash(value)) return
    const text = String(value || '').trim()
    if (!text) {
      handled.current = value
      setParts(emptyAddressParts())
      setNotice(null)
      return
    }
    const known = addressPartsOfText(text)
    if (known) {
      handled.current = value
      setParts(known)
      setNotice(null)
      return
    }
    if (!db) {
      // รอโหลดข้อมูล — แสดงข้อความเดิมในช่องแรกก่อน (ไม่ตั้ง handled เพื่อแยกใหม่เมื่อโหลดเสร็จ)
      setParts({ ...emptyAddressParts(), line: text })
      setNotice(null)
      if (failed) handled.current = value
      return
    }
    const parsed = parseThaiAddress(db, text)
    if (parsed) {
      const composed = composeAddress(parsed)
      handled.current = composed
      rememberAddressParts(composed, parsed)
      setParts(parsed)
      setNotice(squash(composed) === squash(text) ? null : { kind: 'parsed', original: text })
      if (composed !== value) onChange(composed)
    } else {
      handled.current = value
      setParts({ ...emptyAddressParts(), line: text })
      setNotice({ kind: 'unparsed', original: text })
    }
  }, [value, db, failed, onChange])

  const sel = useMemo(() => (db ? resolveAddressSelection(db, parts) : null), [db, parts])
  // ตัวเลือกเดียว = เลือกให้อัตโนมัติ
  useEffect(() => {
    if (sel && !samePartsValue(sel.parts, parts)) emit(sel.parts)
  }, [sel, parts, emit])

  const shown = sel ? sel.parts : parts
  const labels = addressFieldLabels(shown.province)
  const set = (patch: Partial<AddressParts>) => {
    const next = { ...parts, ...patch }
    // ผู้ใช้เริ่มแก้เอง → เลิกแสดงข้อความ "แยกให้แล้ว" · "แยกไม่ได้" หายเมื่อกรอกครบ
    if (notice?.kind === 'parsed' || (notice && isAddressPartsComplete(next))) setNotice(null)
    emit(next)
  }
  const loading = !db && !failed
  const selectDisabled = disabled || !sel

  // ป้ายสถานะ (ต่อท้ายหัวข้อ) บอกสิ่งที่ต้องทำต่อ · บรรทัดล่างเหลือเฉพาะคำแนะนำเสริม
  const badge = disabled ? null : addressStatusBadge(sel, notice?.kind ?? null)
  const zipGlow = !disabled && zipNeedsAttention(sel)
  let hint = ''
  let warn = false
  if (failed) { hint = 'โหลดข้อมูลรหัสไปรษณีย์ไม่ได้ — พิมพ์ที่อยู่เต็มในช่องแรกได้'; warn = true }
  else if (loading) hint = 'กำลังโหลดข้อมูลรหัสไปรษณีย์…'
  else if (sel?.zipState === 'empty' && !shown.province) hint = 'ไม่ทราบรหัส? เลือกจังหวัดเองได้ แล้วระบบเติมรหัสให้'
  else if (sel?.zipState === 'unknown') hint = 'ตรวจรหัสอีกครั้ง หรือเลือกจังหวัดเอง'
  else if (sel?.zipState === 'found' && sel.zipDistrictCount > 1 && !shown.district) hint = `รหัสนี้มี ${sel.zipDistrictCount} อำเภอ/เขต — เลือกด้านล่าง`

  const sheet = variant === 'sheet'
  const ctl = sheet ? '' : 'aurora-input w-full'
  /** หน้าออร์เดอร์ = label แยก (form-label) · แผ่นข้อมูลสมาชิก = label ครอบ (สไตล์ .mo-address-form) */
  const field = (label: string, htmlFor: string, control: ReactNode, extra = '') => sheet
    ? <label className={extra}>{label}{control}</label>
    : (
      <div className={extra}>
        <label htmlFor={htmlFor} className="form-label form-label-required">{label}</label>
        {control}
      </div>
    )

  return (
    <div className="ta-grid">
      {(title || badge) && (
        <div className="ta-head ta-full">
          {title && <span className={sheet ? 'ta-title' : 'form-label form-label-required ta-title'}>{title}</span>}
          {badge && <span className={`ta-badge ta-badge--${badge.tone}`} role="status">{badge.text}</span>}
        </div>
      )}
      {field('รหัสไปรษณีย์', `${id}Zip`, (
        <input id={`${id}Zip`} type="text" inputMode="numeric" autoComplete="off" maxLength={5}
          className={`${ctl}${zipGlow ? ' ta-zip-glow' : ''}`} placeholder="5 หลัก" value={parts.postcode} disabled={disabled}
          aria-invalid={zipGlow || undefined}
          // เปลี่ยนรหัส = ล้างจังหวัด/อำเภอ/ตำบล ให้เลือกใหม่ตามรหัส (กันค่าเก่าค้างกับรหัสที่ไม่ตรง)
          onChange={(e) => set({ postcode: e.target.value.replace(/\D/g, '').slice(0, 5), province: '', district: '', subdistrict: '' })} />
      ))}
      {field('จังหวัด', `${id}Province`, (
        <select id={`${id}Province`} className={ctl} value={shown.province} disabled={selectDisabled}
          onChange={(e) => set({ province: e.target.value, district: '', subdistrict: '' })}>
          <option value="">{loading ? 'กำลังโหลด…' : '— เลือก —'}</option>
          {(sel?.provinces || []).map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      ))}
      {field(labels.district, `${id}District`, (
        <select id={`${id}District`} className={ctl} value={shown.district} disabled={selectDisabled || !shown.province}
          onChange={(e) => set({ district: e.target.value, subdistrict: '' })}>
          <option value="">— เลือก —</option>
          {(sel?.districts || []).map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
      ))}
      {field(labels.subdistrict, `${id}Sub`, (
        <select id={`${id}Sub`} className={ctl} value={shown.subdistrict} disabled={selectDisabled || !shown.district}
          onChange={(e) => set({ subdistrict: e.target.value })}>
          <option value="">— เลือก —</option>
          {(sel?.subdistricts || []).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      ))}
      {field('บ้านเลขที่ หมู่ ซอย ถนน', id, (
        <div className="relative">
          <input
            id={id}
            type="text"
            className={ctl}
            value={parts.line}
            disabled={disabled}
            maxLength={300}
            placeholder="เช่น 99/12 หมู่ 3 ซ.สุขุมวิท 40"
            {...lineProps}
            onChange={(e) => {
              onLineTyping?.()
              set({ line: e.target.value })
            }}
          />
          {lineAddon}
        </div>
      ), `ta-full ${lineWrapClassName || ''}`)}
      {hint && <p className={`ta-hint ta-full${warn ? ' ta-hint--warn' : ''}`}>{hint}</p>}
    </div>
  )
}
